(() => {
  const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const SHOW_DELAY = 120;
  const HIDE_DELAY = 80;
  const HIDE_DELAY_INTERACTIVE = 400;
  const DEFAULT_MORE_LABEL = 'See more';

  function setupHoverCard(drawer, gallery) {
    const { modal, sectionRoot, signal } = drawer;

    const card = document.createElement('div');
    card.className = 'fabric-hover-card';
    card.hidden = true;
    card.setAttribute('aria-hidden', 'true');
    gallery.appendChild(card);

    const seeMore = document.createElement('button');
    seeMore.type = 'button';
    seeMore.className = 'fabric-hover-card__more';
    seeMore.tabIndex = -1;
    seeMore.hidden = true;
    seeMore.textContent = modal.dataset.moreLabel || DEFAULT_MORE_LABEL;

    const contentByColor = new Map();
    let showTimer;
    let hideTimer;
    let activeColor = null;
    let hoveredColor = null;
    let interactive = false;

    function cardVideo() {
      return card.querySelector('video');
    }

    // The card is small: it plays the light rendition, through a single URL
    // that can be dropped again as soon as the card moves on.
    function prepareVideo(video) {
      const sources = video.querySelectorAll('source');
      const src = video.dataset.cardSrc || (sources.length === 1 ? sources[0].getAttribute('src') : '');
      if (!src) return;

      video.replaceChildren();
      video.dataset.src = src;
    }

    function playVideo(video) {
      if (!video) return;

      if (video.dataset.src && !video.getAttribute('src')) video.src = video.dataset.src;
      drawer.activateVideo(video);
    }

    function releaseVideo(video) {
      if (!video) return;

      video.pause();
      if (!video.dataset.src || !video.getAttribute('src')) return;

      video.removeAttribute('src');
      video.load();
    }

    function buildContent(colorLabel) {
      const { media, details } = drawer.findPanels(colorLabel);
      if (!details) return null;

      const inner = document.createElement('div');
      inner.className = 'fabric-hover-card__inner';

      [media, details].forEach((panel) => {
        if (!panel) return;

        const clone = panel.cloneNode(true);
        clone.classList.remove('hidden');
        if (clone.tagName === 'VIDEO') prepareVideo(clone);
        inner.appendChild(clone);
      });

      return inner;
    }

    function getContent(colorLabel) {
      if (!contentByColor.has(colorLabel)) {
        contentByColor.set(colorLabel, buildContent(colorLabel));
      }
      return contentByColor.get(colorLabel);
    }

    function renderContent(content) {
      if (card.firstChild === content) return;

      releaseVideo(cardVideo());
      card.replaceChildren(content, seeMore);
    }

    function measureTruncation() {
      seeMore.hidden = true;

      const description = card.querySelector('.fabric-variant-description');
      const truncated = Boolean(description) && description.scrollHeight > description.clientHeight + 1;

      description?.classList.toggle('is-truncated', truncated);
      return truncated;
    }

    function setInteractive(value) {
      interactive = value;
      card.classList.toggle('is-interactive', value);
      seeMore.hidden = !value;
    }

    function showCard(colorLabel) {
      clearTimeout(hideTimer);

      if (!drawer.isReady()) {
        drawer.load().then((ready) => {
          if (ready && hoveredColor === colorLabel) showCard(colorLabel);
        });
        return;
      }

      const content = colorLabel ? getContent(colorLabel) : null;
      if (!content) {
        hideCard();
        return;
      }

      renderContent(content);

      card.hidden = false;
      activeColor = colorLabel;
      playVideo(cardVideo());

      setInteractive(measureTruncation());
    }

    function hideCard() {
      clearTimeout(hideTimer);
      releaseVideo(cardVideo());
      card.hidden = true;
      activeColor = null;
    }

    // Waits for the pointer to settle, so sweeping across the swatches does
    // not start one video per color on the way.
    function scheduleShow(colorLabel) {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      hoveredColor = colorLabel;

      if (colorLabel === activeColor) return;
      showTimer = setTimeout(() => showCard(colorLabel), SHOW_DELAY);
    }

    function scheduleHide() {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      hoveredColor = null;
      hideTimer = setTimeout(hideCard, interactive ? HIDE_DELAY_INTERACTIVE : HIDE_DELAY);
    }

    function hoverTrigger(event) {
      if (event.pointerType !== 'mouse') return null;

      const trigger = event.target.closest?.('[data-color-value]');
      if (!trigger || trigger.closest('.fabric-drawer-modal')) return null;

      return trigger.contains(event.relatedTarget) ? null : trigger;
    }

    card.addEventListener('pointerenter', () => clearTimeout(hideTimer), { signal });
    card.addEventListener('pointerleave', scheduleHide, { signal });

    seeMore.addEventListener('click', () => {
      const colorLabel = activeColor;
      hideCard();

      if (colorLabel) drawer.selectColor(colorLabel);
      if (typeof modal.show === 'function') modal.show(seeMore);
    }, { signal });

    sectionRoot.addEventListener('pointerover', (event) => {
      const trigger = hoverTrigger(event);
      if (trigger) scheduleShow(trigger.dataset.colorValue);
    }, { signal });

    sectionRoot.addEventListener('pointerout', (event) => {
      if (hoverTrigger(event)) scheduleHide();
    }, { signal });
  }

  function setupDrawer(modal) {
    const sectionId = modal.dataset.sectionId;
    const sectionRoot = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
    const scope = sectionRoot || document;
    const info = modal.querySelector('.fabric-drawer-modal__info');
    const contentUrl = modal.dataset.contentUrl;

    if (!contentUrl && !modal.querySelector('.fabric-details-content-wrapper')) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

    let ready = false;
    let loading = null;
    let items = [];
    let groups = [];
    let activeItem = null;
    let mediaPanels = null;
    let detailsPanels = null;
    let cachedVariants;
    let variantsByColor = null;

    function getVariantSelects() {
      return (sectionId && document.getElementById(`variant-selects-${sectionId}`)) ||
        scope.querySelector('variant-selects');
    }

    function getVariants() {
      if (cachedVariants !== undefined) return cachedVariants;

      const selects = getVariantSelects();
      try {
        if (typeof selects?.getVariantData === 'function') {
          cachedVariants = selects.getVariantData();
        } else {
          const script = scope.querySelector('variant-selects script[type="application/json"]');
          cachedVariants = script ? JSON.parse(script.textContent) : null;
        }
      } catch (e) {
        console.error('Error parsing variants:', e);
        cachedVariants = null;
      }
      return cachedVariants;
    }

    function getVariantsByColor() {
      if (variantsByColor) return variantsByColor;

      const variants = getVariants();
      if (!variants) return null;

      const colors = new Set(items.map((item) => item.dataset.colorValue));
      variantsByColor = new Map();

      variants.forEach((variant) => {
        new Set(variant.options || []).forEach((option) => {
          if (!colors.has(option)) return;

          if (!variantsByColor.has(option)) variantsByColor.set(option, []);
          variantsByColor.get(option).push(variant);
        });
      });

      return variantsByColor;
    }

    function currentOptions() {
      const selects = getVariantSelects();
      if (selects?.options?.length) return selects.options;

      return getVariants()?.[0]?.options;
    }

    function currentColor() {
      return getVariantSelects()?.querySelector('.fieldset--color input[type="radio"]:checked')?.value;
    }

    function isOpen() {
      return modal.hasAttribute('open');
    }

    function isReady() {
      return ready;
    }

    function activateVideo(video) {
      if (!video) return;

      if (video.dataset.poster) {
        video.poster = video.dataset.poster;
        delete video.dataset.poster;
      }

      video.play().catch(() => { });
    }

    function indexPanels(container) {
      if (!container) return null;

      const panels = { byColor: new Map(), active: null };

      container.querySelectorAll('[data-color-label]').forEach((panel) => {
        if (!panels.byColor.has(panel.dataset.colorLabel)) {
          panels.byColor.set(panel.dataset.colorLabel, panel);
        }

        if (panel.classList.contains('hidden')) return;

        if (panels.active) panel.classList.add('hidden');
        else panels.active = panel;
      });

      return panels;
    }

    function findPanels(colorLabel) {
      return {
        media: mediaPanels?.byColor.get(colorLabel),
        details: detailsPanels?.byColor.get(colorLabel)
      };
    }

    function showPanel(panels, colorLabel) {
      if (!panels) return;

      const next = panels.byColor.get(colorLabel) || null;
      const previous = panels.active;

      if (previous && previous !== next) {
        previous.classList.add('hidden');
        if (previous.tagName === 'VIDEO') previous.pause();
      }

      panels.active = next;
      if (!next) return;

      next.classList.remove('hidden');
      if (next.tagName === 'VIDEO' && isOpen()) activateVideo(next);
    }

    function showColor(colorLabel) {
      if (!colorLabel) return;

      showPanel(detailsPanels, colorLabel);
      showPanel(mediaPanels, colorLabel);
    }

    function findItemByVariant(variantId) {
      return items.find((item) => item.getAttribute('data-variant-id') == variantId);
    }

    function setActiveItem(item) {
      if (activeItem && activeItem !== item) {
        activeItem.classList.remove('active');
        activeItem.setAttribute('aria-pressed', 'false');
      }

      activeItem = item || null;
      if (!item) return;

      item.classList.add('active');
      item.setAttribute('aria-pressed', 'true');

      setActiveGroup(item.closest('.fabric-variants-group'));
    }

    function setActiveGroup(activeGroup) {
      if (!groups.length) return;

      // The groups only slide while the drawer is on screen; measuring them
      // closed would force a layout on every variant change for nothing.
      const animate = isOpen() && !reducedMotion.matches;
      const before = animate ? groups.map((group) => group.getBoundingClientRect()) : null;

      groups.forEach((group) => {
        group.classList.toggle('is-active', group === activeGroup);
      });

      if (!before) return;

      groups.forEach((group, index) => {
        const after = group.getBoundingClientRect();
        const dx = before[index].left - after.left;
        const dy = before[index].top - after.top;
        if (!dx && !dy) return;

        group.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
          { duration: 280, easing: 'cubic-bezier(0.2, 0, 0.2, 1)' }
        );
      });
    }

    function selectProductColor(colorValue) {
      if (!colorValue) return;

      const fieldset = scope.querySelector('variant-selects .fieldset--color');
      const input = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || [])
        .find((candidate) => candidate.value === colorValue);

      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function selectColor(colorLabel) {
      const item = items.find((candidate) => candidate.dataset.colorValue === colorLabel);
      if (item) setActiveItem(item);
      showColor(colorLabel);
    }

    function updateAvailableColors(selectedOptions) {
      const byColor = getVariantsByColor();
      if (!byColor) return;

      items.forEach((item) => {
        const colorValue = item.dataset.colorValue;

        const match = (byColor.get(colorValue) || []).find((variant) =>
          variant.options.every((option, index) =>
            option === colorValue || !selectedOptions[index] || option === selectedOptions[index]));

        if (match) item.setAttribute('data-variant-id', match.id);
        item.style.display = match ? '' : 'none';
      });
    }

    function initContent() {
      const detailsContainer = modal.querySelector('.fabric-details-content-wrapper');
      if (!detailsContainer) return false;

      detailsPanels = indexPanels(detailsContainer);
      mediaPanels = indexPanels(modal.querySelector('.fabric-image-container'));
      items = Array.from(modal.querySelectorAll('.fabric-variant-item'));
      groups = Array.from(modal.querySelectorAll('.fabric-variants-group'));
      activeItem = null;
      variantsByColor = null;
      ready = true;

      const options = currentOptions();
      if (options) updateAvailableColors(options);

      const hasVariant = (item) => Boolean(item.getAttribute('data-variant-id'));
      const color = currentColor();

      const initialItem = (color && items.find((item) => item.dataset.colorValue === color && hasVariant(item))) ||
        items.find((item) => item.dataset.selected === 'true' && hasVariant(item)) ||
        items.find(hasVariant);

      if (initialItem) {
        setActiveItem(initialItem);
        showColor(initialItem.dataset.colorValue);
      }

      return true;
    }

    // Fills a drawer that came from the server without its body (see the
    // `fabric-details-drawer` snippet). Resolves to whether the content is usable.
    function load() {
      if (ready) return Promise.resolve(true);
      if (loading) return loading;
      if (!contentUrl || !info) return Promise.resolve(false);

      modal.classList.add('is-loading');

      loading = fetch(contentUrl, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Fabric drawer request failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.getElementById(modal.id)?.querySelector('.fabric-drawer-modal__info');
          if (!source) return false;

          info.innerHTML = source.innerHTML;
          return initContent();
        })
        .catch(() => {
          loading = null;
          return false;
        })
        .finally(() => modal.classList.remove('is-loading'));

      return loading;
    }

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        if (typeof modal.hide === 'function') modal.hide();
        return;
      }

      const item = event.target.closest('.fabric-variant-item');
      if (!item || !item.getAttribute('data-variant-id')) return;

      setActiveItem(item);
      showColor(item.dataset.colorValue);
      selectProductColor(item.dataset.colorValue);
    }, { signal });

    const openObserver = new MutationObserver(() => {
      if (!ready) {
        if (isOpen()) load();
        return;
      }

      const video = mediaPanels?.active;
      if (video?.tagName !== 'VIDEO') return;

      if (isOpen()) activateVideo(video);
      else video.pause();
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });

    document.addEventListener('product:variant-change', (event) => {
      const variant = event.detail?.variant;
      if (!variant || !ready) return;

      const eventSectionId = event.detail.sectionId;
      if (eventSectionId && sectionId && eventSectionId !== sectionId) return;

      if (variant.options) updateAvailableColors(variant.options);

      const item = findItemByVariant(variant.id);
      if (!item) return;

      setActiveItem(item);
      showColor(item.dataset.colorValue);
    }, { signal });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail?.sectionId !== sectionId) return;

      openObserver.disconnect();
      controller.abort();
    }, { signal });

    if (contentUrl) {
      // Reaching for the opener is the earliest sign the drawer is wanted.
      scope.querySelectorAll(`modal-opener[data-modal="#${modal.id}"]`).forEach((opener) => {
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
          opener.addEventListener(type, load, { signal, passive: true });
        });
      });

      // Otherwise the body is fetched once the page has settled, so the drawer
      // is normally filled long before anyone opens it.
      const loadWhenIdle = () => {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(() => load(), { timeout: 3000 });
        else setTimeout(load, 1500);
      };

      if (document.readyState === 'complete') loadWhenIdle();
      else window.addEventListener('load', loadWhenIdle, { once: true, signal });
    } else {
      initContent();
    }

    const gallery = sectionRoot?.querySelector('.product-image-container');

    if (gallery && 'hoverCard' in modal.dataset && window.matchMedia(HOVER_MEDIA_QUERY).matches) {
      setupHoverCard({
        modal,
        sectionRoot,
        signal,
        isReady,
        load,
        findPanels,
        activateVideo,
        selectColor
      }, gallery);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.fabric-drawer-modal').forEach(setupDrawer);
  });
})();
